import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeft, Building2, Shield, Car, Users, FileText,
  BarChart3, AlertTriangle, Loader2, ChevronRight,
  Upload, Download, Trash2, FolderOpen, FileCheck, Eye, Plus
} from 'lucide-react'
import { toast } from 'sonner'
import SuiviChauffeursTab from './SuiviChauffeursTab'
import { PoliceModal } from './C2Polices'
const API = process.env.REACT_APP_BACKEND_URL

const TABS = [
  { id: 'overview',       label: "Vue d'ensemble",  icon: BarChart3 },
  { id: 'polices',        label: 'Polices',         icon: Shield },
  { id: 'documents',      label: 'Base documentaire', icon: FolderOpen },
  { id: 'chauffeurs',     label: 'Suivi chauffeurs', icon: Users },
]

export default function C2ClientDetail() {
  const { tenantId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [tenant, setTenant] = useState(null)
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'overview')
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({})

  useEffect(() => {
    fetchTenant()
    fetchStats()
  }, [tenantId])

  async function fetchTenant() {
    try {
      const res = await fetch(`${API}/api/admin/tenants`)
      const data = await res.json()
      const list = Array.isArray(data) ? data : data.tenants || []
      const found = list.find(t => t.id === tenantId)
      setTenant(found || null)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  async function fetchStats() {
    try {
      const [chRes, sinRes, polRes] = await Promise.all([
        fetch(`${API}/api/sinistres/suivi-chauffeurs?tenant_id=${tenantId}`),
        fetch(`${API}/api/sinistres?tenant_id=${tenantId}`),
        fetch(`${API}/api/polices?tenant_id=${tenantId}`),
      ])
      const [chData, sinData, polData] = await Promise.all([
        chRes.json(), sinRes.json(), polRes.json()
      ])
      setStats({
        chauffeurs: (chData.chauffeurs || []).length,
        cout_flotte: chData.cout_total || 0,
        sinistres: (sinData.sinistres || sinData.data || []).length,
        sinistres_ouverts: (sinData.sinistres || sinData.data || []).filter(s => !s.date_cloture).length,
        polices: (polData.polices || polData.data || []).length,
      })
    } catch (e) {
      console.error(e)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
        <p className="text-sm text-slate-600">Client introuvable</p>
        <button onClick={() => navigate('/c2/clients')} className="mt-3 text-xs text-indigo-600 hover:underline">
          Retour aux clients
        </button>
      </div>
    )
  }

  const tenantAsProp = [{ id: tenant.id, nom: tenant.nom }]

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-detail">
      {/* Header sticky */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-2" data-testid="breadcrumb">
            <button onClick={() => navigate('/c2/clients')} className="hover:text-indigo-600 transition-colors">
              Clients
            </button>
            <span>/</span>
            <span className="text-slate-600 font-medium">{tenant.nom}</span>
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/c2/clients')}
              className="p-1.5 hover:bg-slate-100 rounded-md transition-colors"
              data-testid="back-to-clients"
            >
              <ArrowLeft size={18} className="text-slate-500" />
            </button>
            <div className="w-9 h-9 rounded-md bg-indigo-50 flex items-center justify-center">
              <Building2 size={16} className="text-indigo-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900" data-testid="client-name">
                {tenant.nom}
              </h1>
              <p className="text-xs text-slate-400">
                {tenant.siren && `SIREN: ${tenant.siren}`}
                {tenant.secteur_activite && ` · ${tenant.secteur_activite}`}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 flex items-center gap-1 overflow-x-auto" data-testid="client-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? 'border-indigo-600 text-indigo-600 font-medium'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && (
          <OverviewTab tenant={tenant} stats={stats} onTabChange={setActiveTab} />
        )}
        {activeTab === 'polices' && (
          <PolicesTab tenantId={tenantId} tenants={tenantAsProp} />
        )}
        {activeTab === 'documents' && (
          <DocumentsTab tenantId={tenantId} />
        )}
        {activeTab === 'chauffeurs' && (
          <SuiviChauffeursTab tenantId={tenantId} />
        )}
      </div>
    </div>
  )
}

/* ─── Overview Tab ─── */
function OverviewTab({ tenant, stats, onTabChange }) {
  const currentYear = new Date().getFullYear()
  const cards = [
    { key: 'polices',          label: 'Polices actives',     value: stats.polices || 0,            icon: Shield,   color: 'indigo', tab: 'polices' },
    { key: 'sinistres_ouverts',label: 'Sinistres ouverts',   value: stats.sinistres_ouverts || 0,  icon: FileText, color: 'amber', tab: 'polices' },
    { key: 'chauffeurs',       label: 'Chauffeurs impliques', value: stats.chauffeurs || 0,        icon: Users,    color: 'emerald', tab: 'chauffeurs' },
    { key: 'cout_flotte',      label: 'Cout sinistres flotte', value: `${Number(stats.cout_flotte || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`, icon: Car, color: 'blue', tab: 'chauffeurs' },
  ]

  const colorMap = {
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', icon: 'text-indigo-500' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: 'text-emerald-500' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', icon: 'text-blue-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', icon: 'text-amber-500' },
  }

  return (
    <div className="p-6 space-y-6" data-testid="overview-tab">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => {
          const Icon = c.icon
          const cm = colorMap[c.color]
          return (
            <div
              key={c.key}
              onClick={() => onTabChange(c.tab)}
              className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 hover:shadow-sm cursor-pointer transition-all"
              data-testid={`kpi-${c.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-8 h-8 rounded-md ${cm.bg} flex items-center justify-center`}>
                  <Icon size={15} className={cm.icon} />
                </div>
              </div>
              <p className={`text-2xl font-bold ${cm.text}`}>{c.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{c.label}</p>
            </div>
          )
        })}
      </div>

      {/* Export section */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Exporter le bilan assurance</h3>
            <p className="text-xs text-slate-400 mt-0.5">Document oriente reunion DAF / renouvellement</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open(`${API}/api/export/bilan-pdf/${tenant.id}?annee=${currentYear}`, '_blank')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              data-testid="overview-export-pdf"
            >
              Bilan PDF
            </button>
            <button
              onClick={() => window.open(`${API}/api/export/bilan-csv/${tenant.id}?annee=${currentYear}`, '_blank')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-600 rounded-md hover:bg-slate-50 transition-colors"
              data-testid="overview-export-csv"
            >
              Bilan CSV
            </button>
          </div>
        </div>
      </div>

      {/* Client info */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Informations client</h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Nom</p>
            <p className="text-slate-700 font-medium">{tenant.nom}</p>
          </div>
          {tenant.siren && (
            <div>
              <p className="text-xs text-slate-400 mb-0.5">SIREN</p>
              <p className="text-slate-700">{tenant.siren}</p>
            </div>
          )}
          {tenant.secteur_activite && (
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Secteur</p>
              <p className="text-slate-700">{tenant.secteur_activite}</p>
            </div>
          )}
          {tenant.contact_principal?.email && (
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Email contact</p>
              <p className="text-slate-700">{tenant.contact_principal.email}</p>
            </div>
          )}
          {tenant.contact_principal?.telephone && (
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Telephone</p>
              <p className="text-slate-700">{tenant.contact_principal.telephone}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ─── Polices Tab — list with drill-down to PoliceDetail ─── */
function PolicesTab({ tenantId, tenants = [] }) {
  const navigate = useNavigate()
  const [polices, setPolices] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [compagnies, setCompagnies] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchPolices()
    fetch(`${API}/api/compagnies`).then(r => r.json()).then(d => setCompagnies(d.compagnies || [])).catch(() => {})
  }, [tenantId])

  async function handleCreate(formData) {
    setSaving(true)
    try {
      const res = await fetch(`${API}/api/polices`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) })
      const d = await res.json()
      if (!res.ok) { toast.error(Array.isArray(d.detail) ? d.detail.map(e => e.msg).join(', ') : (d.detail || 'Erreur lors de la creation')); return }
      toast.success('Police creee'); setShowModal(false); fetchPolices()
    } catch { toast.error('Erreur reseau') } finally { setSaving(false) }
  }

  async function fetchPolices() {
    try {
      const res = await fetch(`${API}/api/polices?tenant_id=${tenantId}`)
      if (res.ok) {
        const data = await res.json()
        setPolices(data.polices || data.data || [])
      }
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  const TYPE_LABELS = {
    flotte: 'Flotte auto',
    dab: 'DAB',
    rc_pro: 'RC Pro',
    multirisque: 'Multirisque',
    cyber: 'Cyber',
    rc_transport: 'RC Transport',
    ad_valorem: 'Ad valorem',
    autre: 'Autre',
  }

  function formatDate(d) {
    if (!d) return '--'
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="p-6" data-testid="polices-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">{polices.length} police(s)</p>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" data-testid="btn-nouvelle-police">
          <Plus size={14} /> Nouvelle police
        </button>
      </div>
      {showModal && (
        <PoliceModal compagnies={compagnies} tenants={tenants} forceTenantId={tenantId} onSave={handleCreate} onClose={() => setShowModal(false)} saving={saving} />
      )}

      {polices.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
          <Shield className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Aucune police pour ce client</p>
        </div>
      ) : (
        <div className="space-y-2">
          {polices.map(p => (
            <div
              key={p.id}
              onClick={() => navigate(`/c2/clients/${tenantId}/police/${p.id}`)}
              className="bg-white border border-slate-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm cursor-pointer transition-all group"
              data-testid="police-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-md bg-indigo-50 flex items-center justify-center shrink-0">
                    <Shield size={14} className="text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {p.libelle || p.numero_police}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${
                        p.statut === 'en_cours' ? 'bg-emerald-100 text-emerald-700' :
                        p.statut === 'resilie' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {p.statut === 'en_cours' ? 'En cours' : p.statut || '--'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                      <span>{TYPE_LABELS[p.type_police] || p.type_police}</span>
                      <span>{p.compagnies?.nom || '--'}</span>
                      {p.entites?.nom && <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700" data-testid="police-entite-badge">{p.entites.nom}</span>}
                      <span>{formatDate(p.date_effet)} - {formatDate(p.date_echeance)}</span>
                    </div>
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-indigo-500 transition-colors shrink-0" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Documents Tab — Base documentaire client ─── */
function DocumentsTab({ tenantId }) {
  const [documents, setDocuments] = useState([])
  const [polices, setPolices] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [filterPolice, setFilterPolice] = useState('')
  const [uploadForm, setUploadForm] = useState({ police_id: '', type_document: 'cp' })
  const [showPoliceSelect, setShowPoliceSelect] = useState(false)
  const fileRef = useRef(null)

  useEffect(() => {
    fetchAll()
  }, [tenantId])

  async function fetchAll() {
    setLoading(true)
    try {
      const [docsRes, polRes, catRes] = await Promise.all([
        fetch(`${API}/api/documents-police/all/${tenantId}`),
        fetch(`${API}/api/polices?tenant_id=${tenantId}`),
        fetch(`${API}/api/kb/categories`),
      ])
      if (docsRes.ok) setDocuments((await docsRes.json()).documents || [])
      if (polRes.ok) setPolices((await polRes.json()).polices || [])
      if (catRes.ok) setCategories((await catRes.json()).categories || [])
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    // Auto-detect police from filename - try to match with existing polices
    let targetPoliceId = uploadForm.police_id
    
    // Try to match contract number from filename with existing polices
    if (!targetPoliceId && polices.length > 0) {
      const nums = file.name.match(/(\d{6,})/g) || []
      for (const num of nums) {
        const match = polices.find(p => p.numero_police?.includes(num) || num.includes(p.numero_police))
        if (match) { targetPoliceId = match.id; break }
      }
    }
    
    // If type is 'contrat' or 'cp' (conditions particulières) and no match found,
    // let the backend create a new police automatically
    const isContractType = ['contrat', 'cp', 'conditions_particulieres'].includes(uploadForm.type_document)
    if (!targetPoliceId && !isContractType && polices.length === 1) {
      // For non-contract documents with single police, auto-select it
      targetPoliceId = polices[0].id
    }
    // Si pas de police du tout ou une seule → le backend gere (auto-creation ou auto-select)
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('tenant_id', tenantId)
      fd.append('type_document', uploadForm.type_document)
      if (targetPoliceId) fd.append('police_id', targetPoliceId)
      const res = await fetch(`${API}/api/documents-police/upload`, { method: 'POST', body: fd })
      let data
      try { data = await res.clone().json() } catch { data = { success: false, error: `Erreur serveur (${res.status}). Vérifiez la taille du fichier.` } }
      if (!res.ok || !data.success) {
        toast.error(data.error || data.detail || `Erreur ${res.status}`)
        return
      }
      toast.success('Document uploadé — extraction IA lancée')
      setShowUpload(false)
      fetchAll()
      setTimeout(fetchAll, 12000)
    } catch (err) { toast.error(`Erreur réseau: ${err.message}`) }
    finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function handleDelete(docId) {
    if (!window.confirm('Supprimer ce document ?')) return
    try {
      const res = await fetch(`${API}/api/documents-police/${docId}`, { method: 'DELETE' })
      if (res.ok) {
        toast.success('Document supprime')
        setDocuments(prev => prev.filter(d => d.id !== docId))
      }
    } catch (e) { toast.error('Erreur suppression') }
  }

  async function handleDownload(docId, filename) {
    try {
      const res = await fetch(`${API}/api/documents-police/${docId}/download-url`)
      if (res.ok) {
        const data = await res.json()
        window.open(data.url, '_blank')
      }
    } catch (e) { toast.error('Erreur telechargement') }
  }

  const catLabel = (code) => categories.find(c => c.code === code)?.libelle || code
  const filteredDocs = filterPolice ? documents.filter(d => d.police_id === filterPolice) : documents
  const policeMap = polices.reduce((m, p) => { m[p.id] = p; return m }, {})

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4" data-testid="documents-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-slate-500">{filteredDocs.length} document(s)</p>
          {polices.length > 0 && (
            <select
              value={filterPolice}
              onChange={e => setFilterPolice(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600"
              data-testid="filter-police"
            >
              <option value="">Toutes les polices</option>
              {polices.map(p => (
                <option key={p.id} value={p.id}>{p.libelle || p.numero_police}</option>
              ))}
            </select>
          )}
        </div>
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          data-testid="btn-upload-document"
        >
          <Upload size={13} /> Ajouter un document
        </button>
      </div>

      {/* Upload form — single dropdown */}
      {showUpload && (
        <div className="bg-white border border-indigo-200 rounded-lg p-4 space-y-3" data-testid="upload-form">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Type de document</label>
              <select
                value={uploadForm.type_document}
                onChange={e => setUploadForm(f => ({ ...f, type_document: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-md px-2.5 py-1.5"
                data-testid="upload-type-select"
              >
                {categories.map(c => (
                  <option key={c.code} value={c.code}>{c.libelle}</option>
                ))}
              </select>
            </div>
            {/* Sélection contrat : visible seulement si plusieurs polices et auto-detection echoue */}
            {(showPoliceSelect && polices.length > 1) && (
              <div className="flex-1">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Contrat</label>
                <select
                  value={uploadForm.police_id}
                  onChange={e => { setUploadForm(f => ({ ...f, police_id: e.target.value })); setShowPoliceSelect(false) }}
                  className="w-full text-sm border border-amber-300 bg-amber-50 rounded-md px-2.5 py-1.5"
                  data-testid="upload-police-select"
                >
                  <option value="">Selectionner le contrat...</option>
                  {polices.map(p => (
                    <option key={p.id} value={p.id}>{p.numero_police} — {p.libelle || p.type_police}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="pt-4">
              <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap">
                <Upload size={13} />
                {uploading ? 'Upload...' : 'Choisir un fichier'}
                <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} disabled={uploading} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
              </label>
            </div>
            <div className="pt-4">
              <button onClick={() => { setShowUpload(false); setShowPoliceSelect(false) }} className="text-xs text-slate-500 hover:text-slate-700">Annuler</button>
            </div>
          </div>
          {polices.length === 1 && (
            <p className="text-xs text-slate-400">Contrat auto-detecte : {polices[0].numero_police} — {polices[0].libelle}</p>
          )}
        </div>
      )}

      {/* Documents list */}
      {filteredDocs.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
          <FolderOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Aucun document</p>
          <p className="text-xs text-slate-400 mt-1">Uploadez des contrats, CP, CG pour alimenter l'IA</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredDocs.map(doc => {
            const police = policeMap[doc.police_id]
            const status = doc.statut_extraction
            return (
              <div key={doc.id} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between group hover:border-slate-300 transition-colors" data-testid="document-row">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                    status === 'termine' ? 'bg-emerald-50' : status === 'en_cours' ? 'bg-amber-50' : 'bg-slate-50'
                  }`}>
                    {status === 'termine' ? <FileCheck size={14} className="text-emerald-600" /> :
                     status === 'en_cours' ? <Loader2 size={14} className="text-amber-500 animate-spin" /> :
                     <FileText size={14} className="text-slate-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{doc.nom_fichier}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                      <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{catLabel(doc.type_document)}</span>
                      {police && <span>{police.libelle || police.numero_police}</span>}
                      <span>{new Date(doc.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleDownload(doc.id, doc.nom_fichier)} className="p-1.5 text-slate-400 hover:text-indigo-600 rounded-md hover:bg-indigo-50" title="Telecharger">
                    <Download size={14} />
                  </button>
                  <button onClick={() => handleDelete(doc.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded-md hover:bg-red-50" title="Supprimer">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
